import type {
    DataColumn,
    DataModel,
    ModelKey,
    ReportConfigurationClientLookup,
    ReportConfigurationLookupGroup,
    ReportConfigurationLookupOption,
    RuleOperatorKey,
} from "../types";
import type { RuleOperatorCatalogEntry } from "../logic/operatorCatalog";
import { columnDataTypeCategory } from "../logic/operatorCatalog";
import {
    dedupeLookupOptions,
    scopedDataModelColumnLookupKey,
    dataModelColumnLookupKey,
    labelLookupKey,
} from "../logic/lookupResolution";

export const LOOKUP_ENTITY_KEY_TO_UI_KEYS: Record<string, string[]> = {
    app_enrollment: ["enrollmentStatus"],
    call_disposition: ["disposition"],
    call_type: ["callType"],
    client_zipcode: ["zipCode"],
    county: ["county"],
    grievance_reason: ["grievanceType"],
    grievance_status: ["grievanceStatus"],
    level_of_service: ["los"],
    line_of_business: ["lob"],
    missed_reason: ["cancelReason"],
    rating_category: ["ratingCategory"],
    state: ["state"],
    subcontractor: ["subcontractor", "vendor"],
    treatment_type: ["treatmentType"],
    trip_status: ["tripStatus"],
    yes_no: ["isWillCall"],
};

export function normalizeRuleOperatorRow(raw: any): RuleOperatorCatalogEntry | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const code = readText(raw?.operatorCode ?? raw?.OperatorCode);
    if (!code) return null;
    const flag = (value: unknown) => value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";
    return {
        operatorId: Number(raw?.operatorId ?? raw?.OperatorId) || 0,
        operatorCode: code as RuleOperatorKey,
        label: readText(raw?.label ?? raw?.Label) || code,
        valueModeId: Number(raw?.valueModeId ?? raw?.ValueModeId) || 0,
        appliesToText: flag(raw?.appliesToText ?? raw?.AppliesToText),
        appliesToNumber: flag(raw?.appliesToNumber ?? raw?.AppliesToNumber),
        appliesToDate: flag(raw?.appliesToDate ?? raw?.AppliesToDate),
        sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder) || 0,
    };
}

export function readLookupNumber(value: unknown) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function normalizeLookupOption(raw: any): ReportConfigurationLookupOption | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const value = readText(raw?.value ?? raw?.Value ?? raw?.name ?? raw?.Name);
    const name = readText(raw?.name ?? raw?.Name ?? raw?.value ?? raw?.Value);

    if (!value && !name) return null;

    return {
        id: raw?.id ?? raw?.Id ?? (value || name),
        value: value || name,
        name: name || value,
        sortOrder: raw?.sortOrder ?? raw?.SortOrder,
        clientId: raw?.clientId ?? raw?.ClientId ?? null,
        lookupSourceId: readLookupNumber(raw?.lookupSourceId ?? raw?.LookupSourceId),
        dataModelId: readLookupNumber(raw?.dataModelId ?? raw?.DataModelId),
        dataModelColumnId: readLookupNumber(raw?.dataModelColumnId ?? raw?.DataModelColumnId),
    };
}

export function normalizeLookupGroup(raw: any): ReportConfigurationLookupGroup | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const entityKey = readText(raw?.entityKey ?? raw?.EntityKey);
    if (!entityKey) return null;

    const values = Array.isArray(raw?.values ?? raw?.Values)
        ? (raw.values ?? raw.Values).map(normalizeLookupOption).filter((option: ReportConfigurationLookupOption | null): option is ReportConfigurationLookupOption => !!option)
        : [];

    return {
        entitySourceType: readText(raw?.entitySourceType ?? raw?.EntitySourceType),
        entityKey,
        entityLabel: readText(raw?.entityLabel ?? raw?.EntityLabel),
        lookupSourceId: readLookupNumber(raw?.lookupSourceId ?? raw?.LookupSourceId),
        dataModelId: readLookupNumber(raw?.dataModelId ?? raw?.DataModelId),
        dataModelColumnId: readLookupNumber(raw?.dataModelColumnId ?? raw?.DataModelColumnId),
        values,
    };
}

export function mapLookupGroupsToOptions(groups: ReportConfigurationLookupGroup[]) {
    return groups.reduce((acc, group) => {
        const options = dedupeLookupOptions(group.values);
        if (!options.length) return acc;

        const assign = (key: string) => {
            if (!key) return;
            acc[key] = options;
        };
        const merge = (key: string) => {
            if (!key) return;
            acc[key] = dedupeLookupOptions([...(acc[key] || []), ...options]);
        };

        assign(scopedDataModelColumnLookupKey(group.dataModelId, group.dataModelColumnId));
        assign(dataModelColumnLookupKey(group.dataModelColumnId));
        merge(labelLookupKey(group.entityLabel));
        merge(group.entityKey);
        (LOOKUP_ENTITY_KEY_TO_UI_KEYS[group.entityKey] || []).forEach((uiKey) => {
            merge(uiKey);
        });

        return acc;
    }, {} as Record<string, ReportConfigurationLookupOption[]>);
}

export function lookupValuesFromOptions(optionsByKey: Record<string, ReportConfigurationLookupOption[]>) {
    const next: Record<string, string[]> = {};

    Object.entries(optionsByKey).forEach(([key, options]) => {
        if (Object.values(LOOKUP_ENTITY_KEY_TO_UI_KEYS).some((uiKeys) => uiKeys.includes(key))) {
            next[key] = options.map((option) => option.value);
        }
    });

    return next;
}

export function normalizeReportDataColumn(raw: any): DataColumn | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const label = readText(raw?.label ?? raw?.Label);
    const columnKey = readText(raw?.columnKey ?? raw?.ColumnKey) || label;
    const sqlName = readText(raw?.sqlName ?? raw?.SqlName) || columnKey;
    const idSql = readText(raw?.idColumnName ?? raw?.IdColumnName);

    if (!columnKey || !label) return null;

    const sourceTable = readText(raw?.sourceTable ?? raw?.SourceTable);
    // Set only on a column stored in a related table (see logic/relatedTables.ts).
    const joinKeyColumn = readText(raw?.joinKeyColumn ?? raw?.JoinKeyColumn);
    return {
        dataModelColumnId: Number(raw?.dataModelColumnId ?? raw?.DataModelColumnId) || undefined,
        key: columnKey,
        label,
        sql: sqlName,
        idSql: idSql || undefined,
        role: readText(raw?.columnRole ?? raw?.ColumnRole),
        dataType: readText(raw?.dataType ?? raw?.DataType) || undefined,
        dataTypeCategory: readText(raw?.dataTypeCategory ?? raw?.DataTypeCategory) || undefined,
        isBreakdownable: raw?.isBreakdownable ?? raw?.IsBreakdownable,
        isPeriodAnchor: raw?.isPeriodAnchor ?? raw?.IsPeriodAnchor,
        isOtpComparable: raw?.isOtpComparable ?? raw?.IsOtpComparable,
        isDetailSelectable: raw?.isDetailSelectable ?? raw?.IsDetailSelectable,
        isSensitive: raw?.isSensitive ?? raw?.IsSensitive,
        lookupSourceId: readLookupNumber(raw?.lookupSourceId ?? raw?.LookupSourceId),
        sourceTable: sourceTable || undefined,
        joinKeyColumn: joinKeyColumn || undefined,
        sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder) || 0,
    };
}

export function normalizeReportDataModel(raw: any): { key: ModelKey; model: DataModel } | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const modelKey = readText(raw?.modelKey ?? raw?.ModelKey);
    const label = readText(raw?.label ?? raw?.Label);
    const dataModelId = Number(raw?.dataModelId ?? raw?.DataModelId);
    const columns = Array.isArray(raw?.columns ?? raw?.Columns)
        ? (raw.columns ?? raw.Columns).map(normalizeReportDataColumn).filter((column: DataColumn | null): column is DataColumn => !!column)
        : [];

    if (!modelKey || !label || !Number.isFinite(dataModelId)) return null;

    const sortColumns = (items: DataColumn[]) => [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
    const isCategoryColumn = (column: DataColumn) => String(column.role || "").toLowerCase() === "category";
    const isTimestampColumn = (column: DataColumn) => {
        const role = String(column.role || "").toLowerCase();
        return !!column.isPeriodAnchor || role === "timestamp" || role === "time_stamp" || role === "time stamp" || columnDataTypeCategory(column) === "date";
    };
    const categoryColumns = sortColumns(columns.filter(isCategoryColumn));
    const timestampColumns = sortColumns(columns.filter(isTimestampColumn));
    const visibleColumns = sortColumns(columns);

    return {
        key: modelKey,
        model: {
            dataModelId,
            modelKey,
            label,
            table: readText(raw?.targetTable ?? raw?.TargetTable),
            clientColumn: readText(raw?.clientColumn ?? raw?.ClientColumn) || undefined,
            recordKeyColumn: readText(raw?.recordKeyColumn ?? raw?.RecordKeyColumn) || undefined,
            otpEligible: !!(raw?.otpEligible ?? raw?.OtpEligible),
            sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder) || 0,
            categoryColumns,
            timestampColumns,
            otherColumns: [],
            visibleColumns: visibleColumns.length ? visibleColumns : undefined,
        },
    };
}

export function mapReportDataModels(rawModels: any[]) {
    return rawModels
        .map(normalizeReportDataModel)
        .filter((item: { key: ModelKey; model: DataModel } | null): item is { key: ModelKey; model: DataModel } => !!item)
        .reduce((acc, item) => {
            acc[item.key] = item.model;
            return acc;
        }, {} as Record<ModelKey, DataModel>);
}

export function normalizeReportConfigurationClient(raw: any): ReportConfigurationClientLookup | null {
    const readText = (value: unknown) => value === null || value === undefined ? "" : String(value).trim();
    const clientId = Number(raw?.clientId ?? raw?.ClientId);
    const companyName = readText(raw?.companyName ?? raw?.CompanyName);
    const healthPlanIdentifier = readText(raw?.healthPlanIdentifier ?? raw?.HealthPlanIdentifier);
    const displayName = readText(raw?.displayName ?? raw?.DisplayName) || healthPlanIdentifier || companyName;

    if (!Number.isFinite(clientId) || clientId <= 0 || (!companyName && !displayName)) {
        return null;
    }

    return {
        clientId,
        companyName,
        healthPlanIdentifier,
        displayName,
    };
}